import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Box,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const TrialAccordion = ({ data }) => {
  const {
    briefTitle,
    officialTitle,
    organization,
    trialId,
    registryType,
    studyType,
    enrollmentInfo,
    briefSummary,
    overallStatus,
    primaryOutcomes,
    secondaryOutcomes,
    startDate,
    completionDate,
    conditions,
    keywords,
    interventions,
    arms,
  } = data;

  return (
    <div style={{ maxWidth: "600px", margin: "20px auto" }}>
      {/* Brief Title */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">{briefTitle}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" color="text.secondary">
            {officialTitle}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Sponsor and Basic Information */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Basic Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Sponsor:</strong> {organization?.fullName}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Status and Enrollment</strong>
          </Typography>

          <Box
            sx={{
              display: "inline-block",
              backgroundColor:
                overallStatus === "COMPLETED" ? "error.main" : "success.main",
              color: "white",
              padding: "4px 10px",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: "bold",
              mb: 1,
            }}
          >
            {overallStatus}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Enrollment:</strong> {enrollmentInfo?.count} (
            {enrollmentInfo?.type})
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Trial ID:</strong> {trialId}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Registry Type:</strong> {registryType}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Study Type:</strong> {studyType}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Start Date:</strong>{" "}
            {startDate ? new Date(startDate).toLocaleDateString() : "N/A"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Completion Date:</strong>{" "}
            {completionDate
              ? new Date(completionDate).toLocaleDateString()
              : "N/A"}
          </Typography>

          {conditions?.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Conditions:</strong> {conditions.join(", ")}
            </Typography>
          )}

          {trialId?.startsWith("NCT") && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 0.5,
                mb: 1,
              }}
            >
              <Link
                href={`https://clinicaltrials.gov/study/${trialId}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="primary"
                variant="body1"
              >
                View on ClinicalTrials.gov
              </Link>
              <OpenInNewIcon
                fontSize="small"
                sx={{ ml: 0.5, color: "primary.main" }}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Brief Summary */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Brief Summary</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            {briefSummary}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Keywords */}
      {keywords?.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Keywords</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              {keywords.join(", ")}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Interventions */}
      {interventions?.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Interventions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {interventions.map((intervention, index) => (
              <div key={index}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {intervention.type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {intervention.name}
                </Typography>
                {intervention.description && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Description:</strong> {intervention.description}
                  </Typography>
                )}
                {intervention.armGroupLabels?.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Arm Groups:</strong>{" "}
                    {intervention.armGroupLabels.join(", ")}
                  </Typography>
                )}
                {index < interventions.length - 1 && <Divider sx={{ my: 1 }} />}
              </div>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Study Arms/Groups */}
      {arms?.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Study Arms/Groups</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {arms.map((arm, index) => (
              <div key={index}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Label:</strong> {arm.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {arm.type}
                </Typography>
                {arm.description && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Description:</strong> {arm.description}
                  </Typography>
                )}
                {arm.interventionNames?.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Interventions:</strong>{" "}
                    {arm.interventionNames.join(", ")}
                  </Typography>
                )}
                {index < arms.length - 1 && <Divider sx={{ my: 1 }} />}
              </div>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Primary Outcomes */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Primary Outcomes</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {primaryOutcomes && primaryOutcomes.length > 0 ? (
            primaryOutcomes.map((outcome, index) => (
              <div key={index}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{outcome?.measure}:</strong> {outcome?.description}
                </Typography>

                {index < primaryOutcomes.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </div>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No primary outcomes available
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Secondary Outcomes */}
      {secondaryOutcomes?.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Secondary Outcomes</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {secondaryOutcomes.map((outcome, index) => (
              <div key={index}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{outcome?.measure}:</strong> {outcome?.description}
                </Typography>

                {index < secondaryOutcomes.length - 1 && (
                  <Divider sx={{ my: 1 }} />
                )}
              </div>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

export default TrialAccordion;
